import type { WebsiteSection } from 'src/api/generated';

const getSectionContent = (
  sections: WebsiteSection[] | undefined,
  type: string
): Record<string, unknown> => {
  const content = sections?.find((section) => section.type === type)?.content;

  return content && typeof content === 'object'
    ? (content as Record<string, unknown>)
    : {};
};

/**
 * Resolves the editable text for a structured website section.
 *
 * @param sections - Configured website sections.
 * @param type - Section type to resolve.
 * @param formatter - Legacy/canonical content formatter used without raw input.
 * @returns Raw editor input when present, otherwise formatted section content.
 */
export const getWebsiteSectionInputText = (
  sections: WebsiteSection[] | undefined,
  type: string,
  formatter: (content: Record<string, unknown>) => string
): string => {
  const content = getSectionContent(sections, type);
  const inputText = content.inputText;

  return typeof inputText === 'string' ? inputText : formatter(content);
};

const formatStringItems = (content: Record<string, unknown>): string => {
  const items = content.items;

  return Array.isArray(items)
    ? items
        .filter((item): item is string => typeof item === 'string')
        .join('\n')
    : '';
};

/**
 * Resolves the Services textarea without normalizing in-progress user input.
 *
 * @param sections - Configured website sections.
 * @param legacyServices - Legacy top-level service strings.
 * @returns Raw Services editor input or a backwards-compatible fallback.
 */
export const getWebsiteServicesInputText = (
  sections: WebsiteSection[] | undefined,
  legacyServices: string[] | undefined
): string =>
  getWebsiteSectionInputText(sections, 'services', (content) =>
    legacyServices?.length
      ? legacyServices.join('\n')
      : formatStringItems(content)
  );
